import { Layout } from '@/components/Layout';
import { SHRTLogo } from '@/components/SHRTLogo';
import { 
  APP_VERSION, 
  APP_NAME, 
  APP_BUILD_DATE, 
  DEVELOPER, 
  CHANGELOG, 
  getChangeTypeInfo 
} from '@/config/version';
import { 
  Mail, 
  Code2, 
  Calendar, 
  Tag, 
  ChevronDown, 
  ChevronRight,
  Sparkles,
  Wrench,
  Bug,
  Shield,
  Heart,
  Atom,
  Zap,
  Database,
  Cloud,
  CreditCard,
  LayoutGrid,
  Smartphone,
  Server
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function AboutPage() {
  const [expandedVersions, setExpandedVersions] = useState([APP_VERSION]);

  const toggleVersion = (version) => {
    setExpandedVersions(prev => 
      prev.includes(version) 
        ? prev.filter(v => v !== version)
        : [...prev, version]
    );
  };

  const getChangeIcon = (type) => {
    switch (type) {
      case 'feature': return Sparkles;
      case 'improvement': return Wrench;
      case 'fix': return Bug;
      case 'security': return Shield;
      default: return Tag;
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="flex justify-center mb-4">
            <SHRTLogo size="lg" />
          </div>
          <h1 className="font-['Oswald'] text-2xl sm:text-3xl font-bold uppercase tracking-wider mb-2">
            {APP_NAME}
          </h1>
          <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/20 text-primary font-mono">
              <Tag size={14} />
              v{APP_VERSION}
            </span>
            <span className="inline-flex items-center gap-1">
              <Calendar size={14} />
              {new Date(APP_BUILD_DATE).toLocaleDateString('de-CH')}
            </span>
          </div>
        </div>

        {/* Developer Info */}
        <section className="bg-card border border-border p-4 sm:p-6 mb-6">
          <h2 className="font-['Oswald'] text-lg font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
            <Code2 className="text-primary" size={20} />
            Entwickler
          </h2>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-secondary flex items-center justify-center">
              <span className="font-['Oswald'] text-xl font-bold">TB</span>
            </div>
            <div>
              <p className="font-bold text-lg">{DEVELOPER.name}</p>
              <a 
                href={`mailto:${DEVELOPER.email}`}
                className="text-primary hover:underline flex items-center gap-1 text-sm"
              >
                <Mail size={14} />
                {DEVELOPER.email}
              </a>
            </div>
          </div>
        </section>

        {/* About the App */}
        <section className="bg-card border border-border p-4 sm:p-6 mb-6">
          <h2 className="font-['Oswald'] text-lg font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
            <Heart className="text-primary" size={20} />
            Über diese App
          </h2>
          <div className="prose prose-invert max-w-none text-sm sm:text-base text-foreground/80 space-y-3">
            <p>
              Das <strong>S.H.R.T. Mitgliederportal</strong> ist eine massgeschneiderte Webanwendung 
              für den Swiss Historic Racing Team Verein. Die App ermöglicht die vollständige Verwaltung 
              von Mitgliedern, Events, Fahrzeugen und Finanzen.
            </p>
            <p>
              <strong>Hauptfunktionen:</strong>
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Mitgliederverwaltung mit Monday.com Synchronisation</li>
              <li>Änderungsprotokoll für Profil-Änderungen</li>
              <li>Event-Kalender mit Online-Buchung und Zahlung</li>
              <li>Gast-Anmeldung für Events (ohne Mitgliedschaft)</li>
              <li>Organisator (Kontaktperson) pro Event mit Kontaktdaten</li>
              <li>"Meine Events" für Organisatoren mit Teilnehmerliste und Nachrichten</li>
              <li>E-Mail-Benachrichtigung an Organisator bei Anmeldungen</li>
              <li>QR-Code Tickets für Check-in</li>
              <li>Event-Dateianhänge (PDFs/Bilder als Download-Links)</li>
              <li>Fahrzeugverwaltung mit Bildergalerie, Fan-Stack und Lightbox</li>
              <li>Like-Funktion für Fahrzeuge</li>
              <li>Marktplatz: Fahrzeuge kaufen/verkaufen mit In-App Nachrichten</li>
              <li>Umfrage-System mit anonymer Teilnahme und Echtzeit-Ergebnissen</li>
              <li>Newsletter-System mit Kampagnen, Tracking und WYSIWYG-Editor</li>
              <li>Resend & SMTP E-Mail-Integration (umschaltbar)</li>
              <li>Rechnungsverwaltung mit Stripe Payment Links</li>
              <li>Manuelle Rechnungserstellung für Jahres- und Eintrittsgebühren</li>
              <li>Automatische Rechnungsstornierung bei Mitgliederaustritt</li>
              <li>Stripe Finance Dashboard mit Live-Übersicht</li>
              <li>ClubDesk-Export: camt.053 und CSV, gesamt oder pro Gegenkonto</li>
              <li>Exportprotokoll für nachvollziehbare Buchhaltung</li>
              <li>Dokumentenverwaltung: Statuten, GV-Protokolle, Jahres-/Finanzberichte & Downloads</li>
              <li>Dedizierte iframe-URL für Website-Einbettung (/embed/events)</li>
              <li>Progressive Web App (PWA) mit automatischem Update</li>
            </ul>
          </div>
        </section>

        {/* Tech Stack */}
        <section className="bg-card border border-border p-4 sm:p-6 mb-6">
          <h2 className="font-['Oswald'] text-lg font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
            <Server className="text-primary" size={20} />
            Tech Stack
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { name: 'React', role: 'Frontend', icon: Atom, color: 'text-cyan-400', bg: 'bg-cyan-400/10 border-cyan-400/20' },
              { name: 'FastAPI', role: 'Backend', icon: Zap, color: 'text-emerald-400', bg: 'bg-emerald-400/10 border-emerald-400/20' },
              { name: 'MongoDB', role: 'Datenbank', icon: Database, color: 'text-green-500', bg: 'bg-green-500/10 border-green-500/20' },
              { name: 'Cloudflare R2', role: 'Speicher', icon: Cloud, color: 'text-orange-400', bg: 'bg-orange-400/10 border-orange-400/20' },
              { name: 'Stripe', role: 'Zahlungen', icon: CreditCard, color: 'text-violet-400', bg: 'bg-violet-400/10 border-violet-400/20' },
              { name: 'Monday.com', role: 'CRM-Sync', icon: LayoutGrid, color: 'text-rose-400', bg: 'bg-rose-400/10 border-rose-400/20' },
            ].map((tech) => (
              <div
                key={tech.name}
                className={`flex items-center gap-3 p-3 border rounded-sm ${tech.bg} transition-colors`}
              >
                <tech.icon size={22} className={`${tech.color} flex-shrink-0`} />
                <div className="min-w-0">
                  <p className="font-semibold text-sm leading-tight truncate">{tech.name}</p>
                  <p className="text-xs text-muted-foreground">{tech.role}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
            <Smartphone size={14} className="text-primary flex-shrink-0" />
            <span>Progressive Web App (PWA) — installierbar auf iOS, Android & Desktop</span>
          </div>
        </section>

        {/* Changelog */}
        <section className="bg-card border border-border p-4 sm:p-6">
          <h2 className="font-['Oswald'] text-lg font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
            <Tag className="text-primary" size={20} />
            Changelog
          </h2>
          
          <div className="space-y-3">
            {CHANGELOG.map((release, index) => {
              const isExpanded = expandedVersions.includes(release.version);
              const isLatest = index === 0;
              
              return (
                <div 
                  key={release.version} 
                  className={`border ${isLatest ? 'border-primary/50' : 'border-border'}`}
                >
                  {/* Version Header */}
                  <button
                    onClick={() => toggleVersion(release.version)}
                    className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-muted/50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <span className={`font-mono font-bold ${isLatest ? 'text-primary' : 'text-foreground'}`}>
                        v{release.version}
                      </span>
                      {isLatest && (
                        <span className="text-xs px-2 py-0.5 bg-primary text-primary-foreground uppercase">
                          Aktuell
                        </span>
                      )}
                      <span className="text-sm text-muted-foreground hidden sm:inline">
                        — {release.title}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {new Date(release.date).toLocaleDateString('de-CH')}
                      </span>
                      {isExpanded ? (
                        <ChevronDown size={18} className="text-muted-foreground" />
                      ) : (
                        <ChevronRight size={18} className="text-muted-foreground" />
                      )}
                    </div>
                  </button>
                  
                  {/* Release title on mobile */}
                  {isExpanded && (
                    <div className="sm:hidden px-3 pb-2 -mt-1">
                      <span className="text-sm text-muted-foreground">{release.title}</span>
                    </div>
                  )}
                  
                  {/* Changes List */}
                  {isExpanded && (
                    <div className="px-3 sm:px-4 pb-3 sm:pb-4 border-t border-border/50">
                      <ul className="space-y-2 pt-3">
                        {release.changes.map((change, changeIndex) => {
                          const { label, color } = getChangeTypeInfo(change.type);
                          const Icon = getChangeIcon(change.type);
                          
                          return (
                            <li key={changeIndex} className="flex items-start gap-2 text-sm">
                              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-xs ${color} flex-shrink-0 mt-0.5`}>
                                <Icon size={10} />
                                {label}
                              </span>
                              <span className="text-foreground/80">{change.text}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Footer */}
        <div className="text-center mt-8 text-xs text-muted-foreground">
          <div className="leading-relaxed mb-3">
            <p className="font-bold text-foreground">S.H.R.T.</p>
            <p>Swiss Historic Racing Team</p>
            <p className="text-[10px]">Verein gem&auml;ss Art. 60 ff. ZGB</p>
            <p className="mt-2">Kuno Sch&auml;r &middot; St. Niklausstrasse 1 &middot; CH-4502 Solothurn</p>
            <p>Tel. <a href="tel:+41794153031" className="hover:text-foreground">+41 79 415 30 31</a> &middot; <a href="mailto:info@kunoschaer.ch" className="hover:text-foreground">info@kunoschaer.ch</a></p>
          </div>
          <div className="flex justify-center gap-3 mb-2">
            <Link to="/datenschutz" className="hover:text-foreground">Datenschutz</Link>
            <span>|</span>
            <Link to="/agb" className="hover:text-foreground">AGB</Link>
            <span>|</span>
            <Link to="/impressum" className="hover:text-foreground">Impressum</Link>
          </div>
          <p>&copy; {new Date().getFullYear()} Swiss Historic Racing Team</p>
          <p className="mt-1">
            Mit <Heart className="inline w-3 h-3 text-red-500" /> entwickelt von {DEVELOPER.name}
          </p>
        </div>
      </div>
    </Layout>
  );
}
